import {useCallback, useEffect, useState} from "react";
import {s} from "@/app/utils/s";
import {t} from "i18next";
import {UpdateUserLocationModel} from "@/app/api/user";
import {useGeolocated} from "react-geolocated";

interface CityData {
  name: string;
  lat: number;
  lng: number;
  fullName: string;
}

interface ScreenState {
  hasError?: string
  coords?: UpdateUserLocationModel;
  selectedCity?: CityData;
  isUsingCityCoords: boolean;
}

// Miami coordinates as default
const MIAMI_COORDS: UpdateUserLocationModel = {
  latitude: 25.7617,
  longitude: -80.1918
};

const CITY_CACHE_KEY = 'selectedCity';

const useLocationHooks = () => {
  // Load cached city on initialization
  const loadCachedCity = (): CityData | undefined => {
    try {
      const cached = localStorage.getItem(CITY_CACHE_KEY);
      return cached ? JSON.parse(cached) : undefined;
    } catch {
      return undefined;
    }
  };

  const [state, setState] = useState<ScreenState>(() => {
    const cachedCity = loadCachedCity();
    return {
      hasError: undefined,
      coords: cachedCity ? { latitude: cachedCity.lat, longitude: cachedCity.lng } : MIAMI_COORDS,
      selectedCity: cachedCity,
      isUsingCityCoords: !!cachedCity,
    };
  });

  const {
    coords,
    isGeolocationAvailable,
    isGeolocationEnabled,
    positionError,
  } = useGeolocated({
    positionOptions: { enableHighAccuracy: false },
    userDecisionTimeout: 10000,
  });

  const getCoords = useCallback(() => {
    if (!isGeolocationAvailable) {
      setState(prev => ({
        ...prev,
        hasError: t(s.geolocationNotSupported),
        coords: prev.selectedCity ? { latitude: prev.selectedCity.lat, longitude: prev.selectedCity.lng } : MIAMI_COORDS,
        isUsingCityCoords: true
      }));
    } else if (!isGeolocationEnabled) {
      setState(prev => ({
        ...prev,
        hasError: t(s.geolocationDisabled),
        coords: prev.selectedCity ? { latitude: prev.selectedCity.lat, longitude: prev.selectedCity.lng } : MIAMI_COORDS,
        isUsingCityCoords: true
      }));
    } else if (positionError) {
      setState(prev => ({
        ...prev,
        hasError: positionError.message,
        coords: prev.selectedCity ? { latitude: prev.selectedCity.lat, longitude: prev.selectedCity.lng } : MIAMI_COORDS,
        isUsingCityCoords: true
      }));
    } else if (coords) {
      setState(prev => ({
        ...prev,
        coords: {
          latitude: coords.latitude ?? 0,
          longitude: coords.longitude ?? 0,
        },
        hasError: undefined,
        isUsingCityCoords: false
      }));
    }
  }, [coords, isGeolocationAvailable, isGeolocationEnabled, positionError]);

  const onCitySelect = useCallback((city: CityData) => {
    try {
      localStorage.setItem(CITY_CACHE_KEY, JSON.stringify(city));
    } catch {
      // Ignore localStorage errors
    }
    
    setState(prev => ({
      ...prev,
      selectedCity: city,
      coords: { latitude: city.lat, longitude: city.lng },
      hasError: undefined,
      isUsingCityCoords: true
    }));
  }, []);

  useEffect(() => {
    getCoords();
  }, [coords, isGeolocationAvailable, isGeolocationEnabled, positionError]);

  return {
    stateLocation: state,
    getCoords,
    onCitySelect
  };
};

export default useLocationHooks;